import { poolsideSystemComponents } from "../types";
import * as Types from './Types';
import { HardwareConfig, SiteRecord } from '../config';
import logger from "../utils/Logger";
import Locale from "../utils/Locale";
import { safeJsonParse } from '../utils/Json';
import { noValueSign } from '../types';

export const findState = function(name:string, uuid:string, states:Types.State[]):any {
    let obj = findStateObj(name, uuid, states);
    let result:any = false;
    if (obj)
        result = obj.state;

    return result;
}

export const findStateObj = function(name:string, uuid:string, states:Types.State[]):Types.State {
    let result:Types.State = null;
    
    for (let cState of states) {
        if (    cState.name === name && 
                (!uuid || cState.item === uuid) && 
                (!result || result.updated < cState.updated)
            ) {
                result = cState;
        }
    }

    return result;
}

export const findAllStates = function(uuid:string, states:Types.State[]) {
    let result = {};
    
    for (let cState of states) {
        if (cState.item === uuid) {
            result[ cState.name ] = cState.state;
        }
    }

    return result;
}

/**
 * Injects certain state items if not exists to the state array for backwards compatibility.
 * Allows using the same code accross the application that expect certain states to exist
 * both for old Controllers that do not provide those states and for new Controllers that do.
 * @param states 
 * @param HardwareConfig 
 * @param selectedSite CAN BE REMOVED ONCE POOL-2236 IS DONE
 * @param stateMap 
 * @param getBoardDefinition
 */
export const generateStateItemsForBackwardsCompatibility = function (states:Types.State[], HardwareConfig:HardwareConfig, selectedSite:SiteRecord, stateMap:Object, getBoardDefinition:Function) {
    const autoFilterVersionStateItem = states.find(item => item.item === selectedSite.uuid && item.name === 'AutoFilterVersion');

    // Generating PlatformCapabilities from board definition.
    if (
        autoFilterVersionStateItem && // Have the state item
        Number(autoFilterVersionStateItem.state) && // filter version is set to something which is a number greather than zero
        Number(autoFilterVersionStateItem.state) < 18
    ) {
        logger.info("[PSLib Status] generateStateItemsForBackwardsCompatibility() Generating PlatformCapabilities from board definition.");
        const siteStatesOverride = {
            PlatformCapabilities: JSON.stringify({
                Controllers: HardwareConfig?.Controllers?.map(item => {
                    const boardDefinition = getBoardDefinition(item.UUID);
                    const Bus = [];
                    for (let i = 1; i <= boardDefinition.SerialPortCount; i++) {
                        Bus.push({ PortNumber: i });
                    }
                    const HighVoltage = [];
                    for (let i = 1; i <= boardDefinition.HighVoltagePortCount; i++) {
                        HighVoltage.push({ PortNumber: i });
                    }
                    const LowVoltage = [];
                    for (let i = 1; i <= boardDefinition.LowVoltagePortCount; i++) {
                        LowVoltage.push({ PortNumber: i });
                    }
                    const Actuator = [];
                    for (let i = 1; i <= boardDefinition.ActuatorPortCount; i++) {
                        Actuator.push({ PortNumber: i });
                    }
                    const Thermistor = [];
                    for (let i = 1; i <= boardDefinition.ThermistorPortCount; i++) {
                        Thermistor.push({ PortNumber: i });
                    }
                    const FlowSwitch = [];
                    for (let i = 1; i <= boardDefinition.SwitchPortCount; i++) {
                        FlowSwitch.push({ PortNumber: i });
                    }
                    return ({
                        ControllerUUID: item.UUID,
                        Modules: [ {
                            ModuleNumber: "1",
                            PortTypes: {
                                Bus,
                                HighVoltage,
                                LowVoltage,
                                Actuator,
                                Thermistor,
                                FlowSwitch
                            }
                        } ]
                    })
                })
            } as Types.PlatformCapabilities)
        };
        overrideStates("PSLib, backwards compatibility", siteStatesOverride, selectedSite.uuid, states, stateMap, selectedSite);
    }
    {
        // Convert ModuleNumber of PlatformCapabilities from number to string
        const platformCapabilitiesStateItem = states.find(item => item.item === selectedSite.uuid && item.name === 'PlatformCapabilities');
        // When a site has been just registered it might not have PlatformCapabilities yet
        if (platformCapabilitiesStateItem) {
            const platformCapabilities = (JSON.parse(platformCapabilitiesStateItem.state || "{}") || {}) as Types.PlatformCapabilities;
            if (platformCapabilities) {
                platformCapabilities.Controllers?.forEach(itemController => {
                    itemController.Modules?.forEach(itemModule => {
                        if (typeof itemModule.ModuleNumber === "number") {
                            // @ts-ignore
                            itemModule.ModuleNumber = itemModule.ModuleNumber.toString();
                            logger.info("[PSLib Status] generateStateItemsForBackwardsCompatibility() Converting ModuleNumber to string");
                        }
                    });
                });
                const siteStatesOverride = {
                    PlatformCapabilities: JSON.stringify(platformCapabilities)
                };
                overrideStates("PSLib, backwards compatibility", siteStatesOverride, selectedSite.uuid, states, stateMap, selectedSite);
            }
        }
    }
}

/**
 * Adds/overrides stat items in the state array.
 * @param mockSource String to add to the state item for identify it as a artificially generated state.
 * @param overrides Object holding states values to add or override. E.g:
 * {
 *   AutoFilterVersion: 99,
 *   CatalogVersion: 99,
 *   ChemistryVersion: 99,
 * }
 * @param stateItem UUID of entity the state belongs to. Will be the item prop of the state items.
 * E.g: Uuid of the device or uuid of the site etc...
 * @param states
 * @param stateMap
 * @param selectedSite CAN BE REMOVED ONCE POOL-2236 IS DONE
 */
export const overrideStates = function (mockSource:string, overrides:object, stateItem:string, states:Types.State[], stateMap:Object, selectedSite:SiteRecord) {
    const updated = (new Date()).toISOString();

    Object.entries(overrides).forEach(([ key, value ]) => {
        if (!stateMap[ stateItem ]) stateMap[ stateItem ] = {};
        // Using site uuid instead of siteId because seems Cloud API does not clean up states after removed controllers.
        // It could be multiple site states with the same siteId but with old/invalid `item` prop values..
        const state = states.find(item => item.name === key && item.item === stateItem) as Types.State;
        if (state) {
            state.state = value;
            state.updated = updated;
            state.mockSource = mockSource;
            stateMap[ stateItem ][ key ] = state;
        } else {
            const state = {
                item: stateItem,
                state: value,
                siteId: selectedSite.id,
                name: key,
                mockSource,
                updated
            };
            stateMap[ stateItem ][ key ] = state;
            states.push(state);
        }
    });
}

export function getBowCurrentStateLocalized(value: string): string {
    switch (value) {
    case 'OFF': return Locale.gettext('Off');
    case 'FILTERING': return Locale.gettext('Filtering');
    case 'HEATING': return Locale.gettext('Heating');
    case 'COOLING': return Locale.gettext('Cooling');
    case 'FEATURE': return Locale.gettext('Feature');
    case 'CIRCULATING': return Locale.gettext('Circulating');
    case 'FINISHING_UP': return Locale.gettext('Finishing Up');
    case 'ON': return Locale.gettext('On');
    case 'ERROR': return Locale.gettext('Error');
    case 'CRITICAL_ALERT': return Locale.gettext('Alert');
    case 'COOLDOWN': return Locale.gettext('Cooldown');
    case 'INSTALLER_MODE': return Locale.gettext('Installer Mode');
    default: return value;
    }
}

export function getTemperatureSourceLocalized(source: string): string {
    switch (source) {
    case "THERMISTOR": return Locale.gettext("Thermistor");
    case "INTERNET": return Locale.gettext("Internet");
    case "INTERNET_DUE_TO_DISAGREE": return Locale.gettext(
        "Internet.  This is because the thermistor reading differs from the forecasted temperature by more than %s.",
        Locale.formatTemperatureDiffFromFarenheitDiff(10, 0, "round")
    );
    case "NONE": return Locale.gettext("None");
    default: return source;
    }
} 

export async function processExitModeStrategy(siteState: {[name: string]: any}, getInstallerModeUserInfo: Types.getInstallerModeUserInfoCb, component: poolsideSystemComponents): Promise<{
    isAllowed: boolean,
    details?: string
}> {
    // Site has already left IM
    if (siteState?.Mode === Types.siteMode.NORMAL) {
        return {
            isAllowed: true,
            details: Locale.gettext("The site has already left Installer Mode"),
        };
    }

    const now = new Date();

    const defaultExitStrategyObj = {
        modeExitStrategy: Types.exitModeStrategies.ANYONE,
        modeEnteredByUser: null,
        modeEnterTime: now
    } as Types.ModeExitStrategy;

    const defaultExitStrategyObjRaw = JSON.stringify(defaultExitStrategyObj);

    let ModeExitStrategyObjRaw = siteState?.ModeExitStrategy;
    if (typeof siteState?.ModeExitStrategy !== "string" || siteState?.ModeExitStrategy === "") {
        logger.info("No ModeExitStrategy state, using defaults:", defaultExitStrategyObjRaw);
        ModeExitStrategyObjRaw = defaultExitStrategyObjRaw;
    }
    const modeExitStrategyObj = (safeJsonParse(ModeExitStrategyObjRaw || "{}") ?? defaultExitStrategyObj) as Types.ModeExitStrategy;
    if (modeExitStrategyObj.modeEnterTime) {
        modeExitStrategyObj.modeEnterTime = new Date(modeExitStrategyObj.modeEnterTime);
    }

    try {
        if (!Object.keys(Types.exitModeStrategies).includes(modeExitStrategyObj.modeExitStrategy)) {
            if (typeof modeExitStrategyObj.modeExitStrategy !== "undefined" && modeExitStrategyObj.modeExitStrategy !== null) {
                logger.error(new Error("Unexpected value for ModeExitStrategy.modeExitStrategy: " + modeExitStrategyObj.modeExitStrategy));
            }
            modeExitStrategyObj.modeExitStrategy = Types.exitModeStrategies.ANYONE;
        }
    } catch (err) {
        logger.error(err)
        modeExitStrategyObj.modeExitStrategy = Types.exitModeStrategies.ANYONE;
    }
    
    if (!modeExitStrategyObj.modeEnteredByUser) {
        logger.info("No ModeExitStrategy.modeEnteredByUser, using default null");
        modeExitStrategyObj.modeEnteredByUser = null;
    }

    if (!modeExitStrategyObj.modeEnterTime) {
        logger.info("No ModeExitStrategy.modeEnterTime, using default:", now);
        modeExitStrategyObj.modeEnterTime = now;
    }

    if (component === poolsideSystemComponents.LOCALGUI) {
        // Always alow exit from the Local-GUI
        return { isAllowed: true };
    }

    let userInfo: Types.installeModeUserInfo;
    try {
        userInfo = await getInstallerModeUserInfo(modeExitStrategyObj.modeEnteredByUser);
    } catch (err) {
        logger.error("getExitModeStrategy() Calling getInstallerModeUserInfo() resulted by an error:", err);
    }
    

    switch (modeExitStrategyObj.modeExitStrategy) {
    case Types.exitModeStrategies.LOCAL_ONLY:
        // Reason: called from mobile app, but only allowed to exit from Local-GUI
        return {
            isAllowed: false,
            details: Locale.gettext("Switching out of Installer Mode is currently locked and can only be done by using the touchscreen or service buttons on the Attendant panel itself."),
        };
    case Types.exitModeStrategies.ANYONE:
        let details = "";
        if (userInfo?.userEnteredImLastName) {
            details += Locale.gettext("The system was placed into Installer Mode by %1$s %2$s at %3$s.",
                userInfo?.userEnteredImFirstName || noValueSign,
                userInfo?.userEnteredImLastName || noValueSign,
                Locale.formatDateTime(modeExitStrategyObj.modeEnterTime)
            );
        }
        details += ((details === "" ? "" : "\n\n") + Locale.gettext("Please ensure all servicing has been completed and it is safe to exit Installer Mode."));

        return {
            isAllowed: true,
            details,
        };
    case Types.exitModeStrategies.SAME_USER:
        if (userInfo?.isCurrentUserAllowedToExit) {
            return { isAllowed: true };
        } else {
            return {
                isAllowed: false,
                details: Locale.gettext("Exiting from Installer Mode is currently locked by %1$s %2$s. Please contact your pool professional at %3$s.",
                    userInfo?.userEnteredImFirstName || noValueSign,
                    userInfo?.userEnteredImLastName || noValueSign,
                    userInfo?.siteOrganizationName || noValueSign,
                ) + '\n' + Locale.gettext("You can still exit Installer Mode by using the touchscreen or service buttons on the Attendant panel itself.")
            };
        }
    default:
        // This should never happen.
        // modeExitStrategyObj.modeExitStrategy was set to exitModeStrategies.ANYONE as default in case it has no value or it's value is not a member of `exitModeStrategies`
        logger.error(
            new Error("Unexpected value for modeExitStrategyObj.modeExitStrategy: " + modeExitStrategyObj.modeExitStrategy)
        );

        // We still allow to exit IM as this is the default for program errors
        return {
            isAllowed: true,
            details: Locale.gettext("Unexpected error"),
        };
    }
}

export function getPlatformCapabilities(states: Types.State[], siteUuid: string): Types.PlatformCapabilities {
    const platformCapabilitiesState = states.find(item =>
        item.item === siteUuid && item.name === "PlatformCapabilities"
    );
    const PlatformCapabilitiesRaw = platformCapabilitiesState?.state;
    const PlatformCapabilities = PlatformCapabilitiesRaw ?
        safeJsonParse(PlatformCapabilitiesRaw) as Types.PlatformCapabilities :
        undefined
    ;
    return PlatformCapabilities;
}

/**
 * Checks if the given module has at least BoardRevision 4
 */
export function isAtLeastV4Module(controllerUuid: string, moduleNumber: string, states: Types.State[], siteUuid: string): boolean {
    const platformCapabilities = getPlatformCapabilities(states, siteUuid);
    const boardRevision = platformCapabilities?.Controllers
        ?.find(item => item.ControllerUUID === controllerUuid)
        ?.Modules
        ?.find(item => item.ModuleNumber === moduleNumber)
        ?.ModuleData?.BoardRevision
    ;
    return Number(boardRevision || 0) >= 4;
}

export function getSaltChlorineErrorLocalised(error: Types.SaltChlorinatorErrors): string {
    switch (error) {
    case Types.SaltChlorinatorErrors.AirInLine:
        return Locale.gettext("The device is reporting air trapped in the chlorinator vessel and is not producing chlorine.");
    case Types.SaltChlorinatorErrors.ColdWater:
        return Locale.gettext("The device is reporting: Cold Water.");
    case Types.SaltChlorinatorErrors.HighSalt:
        return Locale.gettext("The device is reporting: High Salt.");
    case Types.SaltChlorinatorErrors.InspectCell:
        return Locale.gettext("The device is reporting: Inspect Cell.");
    case Types.SaltChlorinatorErrors.LowSalt:
        return Locale.gettext("The device is reporting: Low Salt.");
    case Types.SaltChlorinatorErrors.NoFlowWhileGenerating:
        return Locale.gettext("The device is reporting: No Flow.");
    case Types.SaltChlorinatorErrors.PowerFault:
        return Locale.gettext("The device is reporting a power supply fault.");
    case Types.SaltChlorinatorErrors.ProcessorFault:
        return Locale.gettext("The device is reporting a processor fault in the chlorinator power supply.");
    case Types.SaltChlorinatorErrors.VeryLowSalt:
        return Locale.gettext("The device is reporting: Very Low Salt Level.");
    default:
        return Locale.gettext("The device is reporting: %s.", error);
    }
}
